import styled from '@emotion/styled';
import BannerStrip from '../../../components/BannerStrip/BannerStrip';
import { IMPORT_FROM_CHINA } from '../../../routes/routeConstants';
import { FormComponent } from '../../../components/FormCom/FormComponent';
import TrackIcon from '../../../components/Icons/cml/TrackIcon';
import useGoToTrackPage from '../../../hooks/trackPackage/useGoToTrackPage';
import useIntersectionAnimate from '../../../hooks/useIntersectionObserver';
import {
 TrackPackageDataType,
 trackPackageInitialValues,
 TrackPackageSchema,
} from '../../../models/trackPage/trackPackage.model';
import { TextInputField } from '../../../components/FormCom/FormField';
import { FormSubmitButton } from '../../../components/Button/FormSubmitButton';

const Hero = () => {
 useIntersectionAnimate('.intersection-animate', 'animate', {
  threshold: 0.5,
 });
 const { goToTrackPage } = useGoToTrackPage();

 const onSubmit = async (values: TrackPackageDataType) => {
  goToTrackPage(values.trackingId);
 };
 return (
  <Container>
   <WrapImage
    className="web"
    src="/static/img/cml/banner.png"
    alt="hero image"
   />
   <WrapImage
    className="mobile"
    src="/static/img/cml/banner.png"
    alt="hero image"
   />
   <Top>
    <BannerStrip
     text="Shop in bulk from your preferred vendors worldwide and enjoy reliable shipping to Nigeria with CML."
     btnText="Learn more"
     link={`/${IMPORT_FROM_CHINA}`}
    />
   </Top>

   <Content>
    <Headline>
     Track shipments from global Ecommerce stores <b></b>
     <RedText>into Nigeria.</RedText>
    </Headline>

    <SubText>
     We leverage our years of expertise to accelerate and simplify the customs
     clearance process. Experience fast processing time for items stored in our
     safe and secure warehouses and provide an extensive delivery network for
     rapid distribution across Nigeria.
    </SubText>

    <TrackFormContainer>
     <FormComponent
      initialValues={trackPackageInitialValues}
      schema={TrackPackageSchema}
      onSubmit={onSubmit}
      className="inline-responsive"
     >
      <FormHeader>
       <TrackIcon />
       <Label>Track your Parcel</Label>
      </FormHeader>
      <Row>
       <TextInputField
        label=""
        name="trackingId"
        id="trackingId_screenshot"
        type="text"
        placeholder="Insert input field"
       />
       <FormSubmitButton loading={false}>Track</FormSubmitButton>
      </Row>
     </FormComponent>
    </TrackFormContainer>

    <IconsWrapper>
     <IconCard>
      <img src="/static/img/cml/alibaba.png" alt="Alibaba" />
     </IconCard>
     <IconCard>
      <img src="/static/img/cml/shein.png" alt="Shein" />
     </IconCard>
     <IconCard>
      <img src="/static/img/cml/temu.png" alt="Temu" />
     </IconCard>
     <IconCard>
      <img src="/static/img/cml/zara.png" alt="zara" />
     </IconCard>
     <IconCard>
      <img src="/static/img/cml/h_and_m.png" alt="h&m" />
     </IconCard>
    </IconsWrapper>
   </Content>
  </Container>
 );
};

export default Hero;

const Container = styled.div`
 display: flex;
 align-items: center;
 justify-content: center;
 margin: -1rem -5.4375rem 0;
 padding: 3rem 5.4375rem 2rem 5.4375rem;
 position: relative;
 overflow: hidden;

 width: 100vw;
 min-height: calc(100vh - 80px);

 @media (max-width: 884px) {
  padding: 0;
  margin: 0 -24px;
  min-height: calc(100vh - 60px);
 }
`;

const WrapImage = styled.img`
 object-fit: cover;
 position: absolute;
 min-width: 100vw;
 min-height: 110vh;
 top: 4rem;
 bottom: 2rem;

 &.mobile {
  display: none;
 }

 @media (max-width: 884px) {
  &.web {
   display: none;
  }

  &.mobile {
   display: block;
   object-fit: cover;
   object-position: center center;
  }
 }

 @media (max-width: 480px) {
  &.mobile {
   display: block;
   object-fit: cover;
   height: 100%;
   width: 100%;
  }
 }
`;

const Top = styled.div`
 position: absolute;
 top: 0;
 left: 0;
 right: 0;
 z-index: 100;
`;

const Content = styled.div`
 margin-top: 4rem;
 max-width: 1200px;
 text-align: center;
 z-index: 200;

 @media (max-width: 768px) {
  max-width: 100%;
  padding: 0 24px;
 }

 @media (max-width: 480px) {
  margin-top: 5rem;
 }
`;

const Headline = styled.h1`
 font-size: 60px;
 font-weight: 800;
 line-height: 1.4;
 color: #000;

 @media (max-width: 768px) {
  font-size: 38px;
 }

 b {
  font-weight: 700;
 }

 @meia (max-width: 480px) {
  font-size: 28px;
 }
`;

const RedText = styled.span`
 color: ${({ theme }) => theme.palette.redFontColor};
`;

const SubText = styled.p`
 margin-top: 16px;
 font-size: 24px;
 color: #000000;
 line-height: 1.6;

 @media (max-width: 480px) {
  font-size: 18px;
 }
`;

const IconsWrapper = styled.div`
 width: 100%;
 display: grid;
 grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
 place-items: center;
 gap: 24px;
 margin-top: 60px;
 flex-wrap: wrap;
 padding-bottom: 16px;

 @media (max-width: 480px) {
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
 }
`;

const IconCard = styled.div`
 display: flex;
 align-items: center;
 justify-content: center;

 img {
  width: 80%;
  height: auto;
  object-fit: contain;
 }

 @media (max-width: 480px) {
  padding: 0;
  width: 110px;

  img {
   width: 98%;
  }
 }
`;

const TrackFormContainer = styled.div`
 margin-top: 50px;
 width: 100%;
 align-self: center;

 & > * > div,
 & > * > button {
  animation-name: popup;
  animation-duration: 0.7s;
  animation-fill-mode: both;
 }

 & > * > button {
  animation-delay: 0.2s;
  animation-timing-function: ease-in-out;
 }

 @keyframes popup {
  from {
   transform: translateY(50%);
   opacity: 0;
  }
  50% {
   opacity: 0;
  }
  70% {
   opacity: 0.7;
  }
  to {
   transform: none;
   opacity: 1;
  }
 }

 .inline-responsive {
  border-radius: 12px;
  padding: 15px 18px;
  background-color: #e4e4e466;
 }

 .inline-responsive > div {
 }

 .inline-responsive input::placeholder {
  color: #a0a0a0;
 }
`;

const FormHeader = styled.div`
 width: 100%;
 display: flex;
 align-items: center;
 justify-content: flex-start;
 gap: 12px;
 margin-bottom: -20px;
 padding-bottom: 6px;
 border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};

 @media (max-width: 480px) {
  margin-bottom: 2px;
  padding-bottom: 2px;
 }
`;
const Label = styled.p`
 color: #000;
 font-weight: 500;
 font-size: 20px;
`;

const Row = styled.div`
 display: flex;
 align-items: flex-start;
 padding-top: 12px;
 gap: 12px;

 & > button {
  width: 35%;

  @media (max-width: 480px) {
   width: 100%;
  }
 }

 & > div {
  flex: 1;

  & > label {
   display: none;
  }

  & input {
   height: auto;
   padding: 14px 16px;
  }

  @media (max-width: 480px) {
   width: 100%;
  }
 }

 @media (max-width: 480px) {
  flex-direction: column;
 }
`;
